import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Video, BookOpen, MessageSquare, CheckCircle, Lock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODULE_ICONS = {
  video: <Video className="w-6 h-6" />,
  quiz: <BookOpen className="w-6 h-6" />,
  simulator: <MessageSquare className="w-6 h-6" />
};

const MODULE_COLORS = {
  video: 'bg-blue-100 text-blue-600',
  quiz: 'bg-purple-100 text-purple-600',
  simulator: 'bg-orange-100 text-orange-600'
};

const MODULE_TYPE_LABELS = {
  video: 'Vídeo',
  quiz: 'Quiz',
  simulator: 'Simulador'
};

const LessonModules = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lesson, setLesson] = useState(null);
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchData();
  }, [lessonId]);

  const fetchData = async () => {
    // FIX (Simby-3): timeout de 15s + erro visivel com retry.
    // Antes: sem timeout (spinner infinito em cold start/rede movel) e
    // catch silencioso que deixava a tela como "Nenhum modulo disponivel".
    setIsLoading(true);
    setLoadError(false);
    try {
      const [lessonRes, modulesRes] = await Promise.all([
        axios.get(`${API}/lessons/${lessonId}`, { withCredentials: true, timeout: 15000 }),
        axios.get(`${API}/modules?lesson_id=${lessonId}`, { withCredentials: true, timeout: 15000 })
      ]);
      setLesson(lessonRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      console.error('Error fetching lesson modules:', error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getModuleRoute = (mod) => {
    switch (mod.type) {
      case 'video': return `/lesson/${mod.module_id}`;
      case 'quiz': return `/quiz/${mod.module_id}`;
      case 'simulator': return `/simulator/${mod.module_id}`;
      default: return `/quiz/${mod.module_id}`;
    }
  };

  const isModuleCompleted = (moduleId) => {
    return user?.completedLessons?.includes(moduleId);
  };

  const completedCount = modules.filter(m => isModuleCompleted(m.module_id)).length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">📡</div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Não foi possível carregar a lição</h2>
          <p className="text-sm text-gray-500 mb-6">
            Verifique sua conexão e tente novamente. Se o problema continuar, aguarde alguns segundos — o servidor pode estar iniciando.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={fetchData}>
              Tentar novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        {/* Lesson Header */}
        {lesson && (
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">{lesson.title}</h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
              <span>{modules.length} módulo{modules.length !== 1 ? 's' : ''}</span>
              <span>•</span>
              <span>{completedCount}/{modules.length} concluídos</span>
            </div>

            {/* Progress bar */}
            {modules.length > 0 && (
              <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full transition-all duration-500"
                  style={{ width: `${(completedCount / modules.length) * 100}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Module List */}
        {modules.length === 0 ? (
          <Card className="p-8 text-center text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Nenhum módulo disponível ainda.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {modules.map((mod, index) => {
              const completed = isModuleCompleted(mod.module_id);
              const previousCompleted = index === 0 || isModuleCompleted(modules[index - 1].module_id);
              const isLocked = (lesson?.sequential_modules || lesson?.sequentialModules) && !previousCompleted;

              return (
                <Card
                  key={mod.module_id}
                  className={`p-5 transition-all duration-200 ${
                    isLocked
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:shadow-lg cursor-pointer hover:border-purple-300'
                  } ${completed ? 'border-green-200 bg-green-50' : ''}`}
                  onClick={() => !isLocked && navigate(getModuleRoute(mod))}
                >
                  <div className="flex items-center gap-4">
                    {/* Order badge */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                      completed
                        ? 'bg-green-500 text-white'
                        : `${MODULE_COLORS[mod.type] || MODULE_COLORS.quiz}`
                    }`}>
                      {completed ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : isLocked ? (
                        <Lock className="w-4 h-4" />
                      ) : (
                        MODULE_ICONS[mod.type] || MODULE_ICONS.quiz
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {MODULE_TYPE_LABELS[mod.type] || mod.type}
                        </span>
                        {completed && (
                          <span className="text-xs text-green-600 font-medium">Concluído</span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 truncate">{mod.title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {mod.type === 'video' ? '🪣🪣🪣 +1pt' : mod.type === 'quiz' ? '💧 +2pts' : '🎭 Simulador'}
                      </p>
                    </div>

                    {/* Arrow */}
                    {!isLocked && (
                      <div className="text-gray-400 flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonModules;
